import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { groupBy } from 'lodash';

import {
  HorizontalTabs,
  Avatar,
  H2,
  Icon,
  IconButton,
  Input,
  InputBox,
  LetterAvatar,
  Modal,
  Text
} from '../../../../../components';

const ModalHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  height: 40px;
`;

const SourcesListWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-top: 15px;
  min-height: 200px;
`;

const Integration = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  margin-bottom: 8px;
  padding: 5px 0 5px 5px;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  &:hover {
    background: var(--elevation-low);
    border-radius: 8px;
  }
`;

const SearchWrapper = styled.div`
  margin-top: 15px;
`;

const StyledIcon = styled(Icon)`
  opacity: 0.8;
`;

const StyledHorizontalTabs = styled(HorizontalTabs)`
  padding: 0;
`;

const schemaToTitle = {
  accounting: 'Accounting',
  crm: 'CRM',
  hris: 'HR',
  payments: 'Payments',
  marketing: 'Marketing',
  product: 'Product',
  cloud_tables: 'Cloud Tables',
  support_ticketing: 'Support Ticketing'
};

const productIntegrations = [
  {
    id: 'psql_direct_db',
    displayName: 'PostgreSQL',
    name: 'psql_direct_db-supper',
    picture: null,
    schema: 'product'
  },
  {
    id: 'mysql_direct_db',
    displayName: 'MySQL',
    name: 'mysql_direct_db-supper',
    picture: null,
    schema: 'product'
  },
  {
    id: 'snowflake_direct_db',
    displayName: 'Snowflake',
    name: 'snowflake_direct_db-supper',
    picture: null,
    schema: 'product'
  }
];

const AddSourceModal = ({ integrations, handleIntegrationSelection }) => {
  const [activeGroup, setActiveGroup] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const groupedIntegrations = useMemo(() => {
    const grouped = groupBy(
      integrations?.filter((i) => i.schema !== 'ticketing'),
      'schema'
    );

    return { ...grouped, product: productIntegrations };
  }, [integrations]);

  useEffect(() => {
    if (groupedIntegrations) {
      setActiveGroup(Object.keys(groupedIntegrations).sort()[0]);
    }
  }, [groupedIntegrations]);

  useEffect(() => {
    if (activeGroup) {
      setSearchResults(groupedIntegrations[activeGroup].slice(0, 5));
    }
  }, [activeGroup]);

  const filterIntegrations = useCallback(
    (q) => {
      const integrationsGroup = groupedIntegrations[activeGroup];

      if (q.length > 1 && integrationsGroup != null) {
        setSearchResults(
          integrationsGroup
            .filter((i) => i.displayName.toLowerCase().startsWith(q.toLowerCase()))
            .slice(0, 5)
        );
      }
      if (q.length === 0) {
        setSearchResults(integrationsGroup.slice(0, 5));
      }
    },
    [activeGroup, groupedIntegrations]
  );

  return (
    <Modal size='md' bodyStyle={{ padding: 20 }}>
      {(close) => (
        <>
          <ModalHeaderWrapper>
            <H2>Connect new source</H2>
            <IconButton small naked iconName='close' onClick={close} iconDisplaySize={24} />
          </ModalHeaderWrapper>
          {groupedIntegrations && (
            <StyledHorizontalTabs
              initialActiveTabIndex={0}
              tabs={Object.keys(groupedIntegrations)
                .sort()
                .map((schema) => ({
                  title: schema,
                  component: (
                    <>
                      {schemaToTitle[schema]} ({groupedIntegrations[schema]?.length})
                    </>
                  ),
                  onClick: () => setActiveGroup(schema)
                }))}
            />
          )}
          {groupedIntegrations[activeGroup]?.length > 5 && (
            <SearchWrapper>
              <InputBox>
                <StyledIcon name='search' size={24} />
                <Input
                  placeholder='Search by name'
                  onChange={(evt) => filterIntegrations(evt.target.value)}
                />
              </InputBox>
            </SearchWrapper>
          )}

          <SourcesListWrapper>
            {searchResults.map((i) => (
              <Integration onClick={() => handleIntegrationSelection(i, close)} key={i.id}>
                {i.picture ? (
                  <Avatar width={40} src={i.picture} />
                ) : (
                  <LetterAvatar size={40} name={i.displayName} />
                )}
                <Text>{i.displayName}</Text>
              </Integration>
            ))}
          </SourcesListWrapper>
        </>
      )}
    </Modal>
  );
};

AddSourceModal.propTypes = {
  integrations: PropTypes.arrayOf(
    PropTypes.shape({
      displayName: PropTypes.string,
      picture: PropTypes.string
    })
  ).isRequired,
  handleIntegrationSelection: PropTypes.func.isRequired
};

export { AddSourceModal };
